import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { faCalendarClock } from '@fortawesome/pro-regular-svg-icons/faCalendarClock';
import { faQuestionCircle } from '@fortawesome/pro-regular-svg-icons/faQuestionCircle';
import { faCircleQuestion } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Button,
  Form,
  Input,
  Row,
  Select,
  Space,
  theme,
  Tooltip,
  Typography,
} from 'antd';
import { PosModal } from 'pos-client/components/shared/Display/Modal/PosModal.tsx';
import { createFormFields } from 'pos-client/components/shared/Form/FormUtils.ts';
import { PosCheckbox } from 'pos-client/components/shared/Form/PosCheckbox.tsx';
import { PosFormItem } from 'pos-client/components/shared/Form/PosFormItem.tsx';
import { useNotifications } from 'pos-client/hooks/useNotifications.tsx';
import { usePermissions } from 'pos-client/hooks/usePermissions.tsx';
import { trpc } from 'pos-client/utils/trpc.ts';
import {
  type JobTypeCreateFERequestModel,
  JobTypeCreateFERequestSchema,
  type JobTypeModel,
} from 'pos-shared/models/jobTypeSchema.ts';

interface CreateEditJobTypesModalProps {
  isEdit: boolean;
  modalIsOpen: boolean;
  handleModalClose: () => void;
  jobType: JobTypeModel | null;
}

export const CreateEditJobTypesModal = ({
  modalIsOpen,
  handleModalClose,
  isEdit,
  jobType,
}: CreateEditJobTypesModalProps) => {
  const { t } = useTranslation();
  const { showSuccess, showError } = useNotifications();
  const { token } = theme.useToken();
  const { hasPermissions } = usePermissions();
  const trpcContext = trpc.useUtils();

  const canAddJobType = hasPermissions('add.edit.job.types');

  const form = useForm<JobTypeCreateFERequestModel>({
    resolver: zodResolver(JobTypeCreateFERequestSchema),
    defaultValues: {
      name: jobType?.name ?? '',
      serviceType: jobType?.serviceType ?? 'Static',
      description: jobType?.description ?? '',
      isActive: jobType?.isActive ?? true,
      isSingleItem: jobType?.isSingleItem ?? false,
    },
  });
  const formFields = createFormFields(form);

  const statusOptions = useMemo(() => {
    return [
      { label: t('statuses.active'), value: true },
      { label: t('statuses.inactive'), value: false },
    ];
  }, []);

  const serviceTypeOptions = useMemo(() => {
    return [
      { label: t('statuses.static'), value: 'Static' },
      { label: t('statuses.mobile'), value: 'Mobile' },
    ];
  }, []);

  const handleReset = () => {
    handleModalClose();
    form.reset();
  };

  const { mutate: createJobType, isLoading: isCreating } =
    trpc.createJobType.useMutation({
      onSuccess: async () => {
        showSuccess(
          t(`common.notification.success.message`, {
            action: 'created',
            entity: 'job type',
          })
        );

        await trpcContext.findJobTypes.invalidate();
        handleReset();
        handleModalClose();
      },
      onError: (error) => {
        if (error.message) {
          showError(error.message);
        }
      },
    });

  const { mutate: updateJobType, isLoading: isUpdating } =
    trpc.updateJobType.useMutation({
      onSuccess: async () => {
        showSuccess(
          t(`common.notification.success.message`, {
            action: 'updated',
            entity: 'job type',
          })
        );
        await trpcContext.findJobTypes.invalidate();
        handleModalClose();
      },
    });

  const handleCreate = () => {
    form.handleSubmit((data) => {
      createJobType(data);
    })();
  };

  const handleEdit = () => {
    if (jobType) {
      form.handleSubmit(async (data) => {
        await updateJobType({
          ...data,
          id: jobType.id,
        });
      })();
    }
  };

  const isLoading = isCreating || isUpdating;

  return (
    <PosModal
      header={{
        title: isEdit ? t('jobTypes.editJobType') : t('jobTypes.createJobType'),
        icon: faCalendarClock,
      }}
      open={modalIsOpen}
      onCancel={handleReset}
      footer={
        <Space>
          <Button onClick={handleReset}>{t('common.buttons.cancel')}</Button>
          <Button
            key="submit"
            type="primary"
            onClick={isEdit ? handleEdit : handleCreate}
            loading={isLoading}
            disabled={!form.formState.isValid || !canAddJobType}
          >
            {isEdit ? t('common.buttons.save') : t('common.buttons.create')}
          </Button>
        </Space>
      }
    >
      <Form
        fields={formFields}
        layout="vertical"
        style={{
          marginTop: token.marginMD,
          display: 'flex',
          flexDirection: 'column',
          gap: token.margin,
        }}
      >
        <PosFormItem
          label={
            <>
              <Typography.Text style={{ marginRight: token.marginXS }}>
                {t('jobTypes.jobName')}
              </Typography.Text>
              <Tooltip title={t('jobTypes.jobNameTooltip')}>
                <FontAwesomeIcon icon={faQuestionCircle} />
              </Tooltip>
            </>
          }
          control={form.control}
          name="name"
          required
        >
          <Input
            disabled={isEdit}
            placeholder={t('jobTypes.jobNamePlaceholder')}
            aria-label={t('jobTypes.jobName')}
          />
        </PosFormItem>
        <PosFormItem
          label={t('common.words.description')}
          control={form.control}
          name="description"
          required
        >
          <Input
            placeholder={t('jobTypes.jobDescriptionPlaceholder')}
            aria-label={t('common.words.description')}
            disabled={!canAddJobType}
          />
        </PosFormItem>
        <PosFormItem
          label={t('jobTypes.serviceType')}
          name="serviceType"
          control={form.control}
          required
        >
          <Select
            placeholder={t('jobTypes.selectServiceType')}
            options={serviceTypeOptions}
            disabled={isEdit}
            aria-label={t('jobTypes.selectServiceType')}
          ></Select>
        </PosFormItem>
        <PosFormItem
          label={t('common.status.status')}
          name="isActive"
          control={form.control}
          required
        >
          <Select
            placeholder={t('common.status.selectStatus')}
            options={statusOptions}
            aria-label={t('common.status.selectStatus')}
            disabled={!canAddJobType}
          ></Select>
        </PosFormItem>
        <Row align="middle">
          <PosCheckbox control={form.control} name="isSingleItem" noStyle>
            {t('jobTypes.singleItem')}
          </PosCheckbox>
          <Tooltip title={t('jobTypes.singleItemTooltip')}>
            <FontAwesomeIcon icon={faCircleQuestion} />
          </Tooltip>
        </Row>
      </Form>
    </PosModal>
  );
};
